import type { AudioRecorder, RecordingResult } from "./types";

/**
 * Web implementation of AudioRecorder using MediaRecorder API.
 */
export function createWebAudioRecorder(): AudioRecorder {
  let mediaRecorder: MediaRecorder | null = null;
  let audioChunks: Blob[] = [];
  let stream: MediaStream | null = null;
  let analysisStream: MediaStream | null = null;
  let audioContext: AudioContext | null = null;
  let analyser: AnalyserNode | null = null;
  let startTime = 0;
  let pausedDuration = 0;
  let pauseStartTime = 0;
  let _isRecording = false;
  let _isPaused = false;
  let mimeType = "";

  const levelListeners = new Set<(level: number) => void>();
  let animationFrameId: number | null = null;

  function startLevelMeter() {
    if (!analyser) return;

    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    const update = () => {
      if (!analyser || !_isRecording || _isPaused) {
        animationFrameId = requestAnimationFrame(update);
        return;
      }

      analyser.getByteFrequencyData(dataArray);
      const sum = dataArray.reduce((a, b) => a + b, 0);
      const average = sum / dataArray.length;
      const normalized = Math.min(average / 128, 1);

      for (const listener of levelListeners) {
        listener(normalized);
      }

      animationFrameId = requestAnimationFrame(update);
    };

    animationFrameId = requestAnimationFrame(update);
  }

  function stopLevelMeter() {
    if (animationFrameId !== null) {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
    }
  }

  function cleanup() {
    stopLevelMeter();

    if (stream) {
      for (const track of stream.getTracks()) {
        track.stop();
      }
      stream = null;
    }

    if (analysisStream) {
      for (const track of analysisStream.getTracks()) {
        track.stop();
      }
      analysisStream = null;
    }

    if (audioContext?.state !== "closed") {
      audioContext?.close();
    }
    audioContext = null;
    analyser = null;
    mediaRecorder = null;
    audioChunks = [];
    _isRecording = false;
    _isPaused = false;
  }

  return {
    async requestPermission(): Promise<boolean> {
      try {
        const testStream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        for (const track of testStream.getTracks()) {
          track.stop();
        }
        return true;
      } catch {
        return false;
      }
    },

    async hasPermission(): Promise<boolean> {
      try {
        const result = await navigator.permissions.query({
          name: "microphone" as PermissionName,
        });
        return result.state === "granted";
      } catch {
        // Permissions API not supported, try to get stream
        return this.requestPermission();
      }
    },

    async start(): Promise<void> {
      cleanup();

      stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Clone the stream for analysis to avoid interference with MediaRecorder
      analysisStream = stream.clone();

      // Set up audio analysis for level meter using cloned stream
      audioContext = new AudioContext();
      if (audioContext.state === "suspended") {
        await audioContext.resume();
      }

      const source = audioContext.createMediaStreamSource(analysisStream);
      analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);

      // Determine supported mimeType
      if (MediaRecorder.isTypeSupported("audio/webm")) {
        mimeType = "audio/webm";
      } else if (MediaRecorder.isTypeSupported("audio/mp4")) {
        mimeType = "audio/mp4";
      } else {
        mimeType = "";
      }

      mediaRecorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      mimeType = mediaRecorder.mimeType;
      audioChunks = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.push(event.data);
        }
      };

      mediaRecorder.start(100);
      startTime = Date.now();
      pausedDuration = 0;
      _isRecording = true;
      _isPaused = false;

      startLevelMeter();
    },

    async pause(): Promise<void> {
      if (mediaRecorder?.state === "recording") {
        mediaRecorder.pause();
        pauseStartTime = Date.now();
        _isPaused = true;
      }
    },

    async resume(): Promise<void> {
      if (mediaRecorder?.state === "paused") {
        mediaRecorder.resume();
        pausedDuration += Date.now() - pauseStartTime;
        _isPaused = false;
      }
    },

    async stop(): Promise<RecordingResult> {
      return new Promise((resolve, reject) => {
        if (!mediaRecorder) {
          reject(new Error("No recording in progress"));
          return;
        }

        const recorder = mediaRecorder;
        const recordedMimeType = mimeType;

        recorder.onstop = () => {
          const blob = new Blob(audioChunks, { type: recordedMimeType });
          const duration = (Date.now() - startTime - pausedDuration) / 1000;

          // Create blob URL
          const uri = URL.createObjectURL(blob);

          cleanup();

          resolve({
            uri,
            duration,
            mimeType: recordedMimeType,
            size: blob.size,
            blob, // Include raw blob for creating File objects
          });
        };

        recorder.onerror = () => {
          cleanup();
          reject(new Error("Recording failed"));
        };

        recorder.stop();
      });
    },

    async cancel(): Promise<void> {
      if (mediaRecorder) {
        mediaRecorder.onstop = null;
        mediaRecorder.stop();
      }
      cleanup();
    },

    isRecording(): boolean {
      return _isRecording && !_isPaused;
    },

    getDuration(): number {
      if (!_isRecording) return 0;
      const elapsed = Date.now() - startTime;
      const paused = _isPaused ? Date.now() - pauseStartTime : 0;
      return (elapsed - pausedDuration - paused) / 1000;
    },

    onLevelUpdate(callback: (level: number) => void): () => void {
      levelListeners.add(callback);
      return () => levelListeners.delete(callback);
    },
  };
}
